import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, GraduationCap, Plus, Edit, Trash2, Search } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { useToast } from '../../../hooks/useToast';

interface AcademicProgram {
  id: number;
  code: string;
  name: string;
  level: 'carrera_profesional' | 'curso_profesional' | 'especialidad' | 'curso_corto' | 'clase_un_dia' | 'diplomado' | 'licenciatura' | 'certificado' | 'otro';
  description?: string;
  duration?: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

interface CurriculumCourse {
  id: number;
  code: string;
  name: string;
  description?: string;
  credits: number;
}

interface ProgramCourse {
  courseId: number;
  sequenceChainId?: string | null;
  sequenceOrder?: number | null;
}

const LEVEL_LABELS: Record<string, string> = {
  carrera_profesional: 'Carrera Profesional',
  curso_profesional: 'Curso Profesional',
  especialidad: 'Especialidad',
  curso_corto: 'Curso Corto',
  clase_un_dia: 'Clase de un Día',
};

export default function Programas() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [programs, setPrograms] = useState<AcademicProgram[]>([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingProgram, setEditingProgram] = useState<AcademicProgram | null>(null);
  const [availableCourses, setAvailableCourses] = useState<CurriculumCourse[]>([]);
  const [selectedCourses, setSelectedCourses] = useState<ProgramCourse[]>([]);
  const [courseSearchTerm, setCourseSearchTerm] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState<{
    code: string;
    name: string;
    level: string;
    description: string;
    duration: string;
    isActive: boolean;
  }>({
    code: '',
    name: '',
    level: 'carrera_profesional',
    description: '',
    duration: '',
    isActive: true,
  });

  useEffect(() => {
    fetchPrograms();
    fetchCourses();
  }, []);

  const fetchPrograms = async () => {
    try {
      const response = await fetch('/api/academic-programs', {
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        const sorted = Array.isArray(data) ? data.sort((a: AcademicProgram, b: AcademicProgram) => a.code.localeCompare(b.code, 'es')) : data;
        setPrograms(sorted);
      }
    } catch (error) {
      console.error('Error fetching programs:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'No se pudieron cargar los programas académicos',
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchCourses = async () => {
    try {
      const response = await fetch('/api/curriculum-courses', {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setAvailableCourses(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      console.error('Error fetching courses:', error);
    }
  };

  const fetchProgramCourses = async (programId: number) => {
    try {
      const response = await fetch(`/api/academic-programs/${programId}/courses`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        const courses: ProgramCourse[] = data.map((c: any) => ({
          courseId: c.courseId,
          sequenceChainId: c.sequenceChainId || null,
          sequenceOrder: c.sequenceOrder || null,
        }));
        setSelectedCourses(courses);
      }
    } catch (error) {
      console.error('Error fetching program courses:', error);
      setSelectedCourses([]);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const url = editingProgram
        ? `/api/academic-programs/${editingProgram.id}`
        : '/api/academic-programs';
      
      const method = editingProgram ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          ...formData,
          duration: formData.duration ? parseInt(formData.duration) : undefined,
        }),
      });

      if (response.ok) {
        const savedProgram = await response.json();
        const programId = editingProgram ? editingProgram.id : savedProgram.id;
        
        await fetch(`/api/academic-programs/${programId}/courses`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ courses: selectedCourses }),
        });

        toast({
          title: 'Éxito',
          description: editingProgram
            ? 'Programa actualizado correctamente'
            : 'Programa creado correctamente',
        });
        setIsDialogOpen(false);
        resetForm();
        fetchPrograms();
      } else {
        const error = await response.json();
        toast({
          variant: 'destructive',
          title: 'Error',
          description: error.error || 'Ocurrió un error',
        });
      }
    } catch (error) {
      console.error('Error saving program:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'No se pudo guardar el programa',
      });
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm('¿Estás seguro de eliminar este programa académico?')) return;

    try {
      const response = await fetch(`/api/academic-programs/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (response.ok) {
        toast({
          title: 'Éxito',
          description: 'Programa eliminado correctamente',
        });
        fetchPrograms();
      } else {
        const error = await response.json();
        toast({
          variant: 'destructive',
          title: 'Error',
          description: error.error || 'No se pudo eliminar el programa',
        });
      }
    } catch (error) {
      console.error('Error deleting program:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'No se pudo eliminar el programa',
      });
    }
  };

  const handleEdit = async (program: AcademicProgram) => {
    setEditingProgram(program);
    setFormData({
      code: program.code,
      name: program.name,
      level: program.level,
      description: program.description || '',
      duration: program.duration?.toString() || '',
      isActive: program.isActive,
    });
    await fetchProgramCourses(program.id);
    setIsDialogOpen(true);
  };

  const resetForm = () => {
    setEditingProgram(null);
    setFormData({
      code: '',
      name: '',
      level: 'carrera_profesional',
      description: '',
      duration: '',
      isActive: true,
    });
    setSelectedCourses([]);
  };

  const toggleCourse = (courseId: number, checked: boolean) => {
    if (checked) {
      setSelectedCourses([...selectedCourses, { courseId, sequenceChainId: null, sequenceOrder: null }]);
    } else {
      setSelectedCourses(selectedCourses.filter(c => c.courseId !== courseId));
    }
  };

  const updateCourseSequence = (courseId: number, field: 'sequenceChainId' | 'sequenceOrder', value: string | number | null) => {
    setSelectedCourses(selectedCourses.map(c => 
      c.courseId === courseId ? { ...c, [field]: value } : c
    ));
  };

  const isCourseSelected = (courseId: number) => selectedCourses.some(c => c.courseId === courseId);
  
  const getCourseSequence = (courseId: number) => selectedCourses.find(c => c.courseId === courseId);

  return (
    <div className="p-6">
      <button
        onClick={() => navigate('/app/catalogos')}
        className="mb-4 flex items-center text-gray-600 hover:text-gray-900 font-medium"
      >
        <ArrowLeft className="h-4 w-4 mr-2" />
        Volver a Catálogos
      </button>

      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Programas Académicos</h1>
          <p className="text-gray-600">Gestión de carreras y planes de estudio</p>
        </div>
        <Button
          onClick={() => {
            resetForm();
            setIsDialogOpen(true);
          }}
        >
          <Plus className="h-4 w-4 mr-2" />
          Nuevo Programa
        </Button>
      </div>

      <div className="mb-4">
        <input
          type="text"
          placeholder="Buscar por código, nombre..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full max-w-md px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>

      {loading ? (
        <div className="text-center py-12">
          <p className="text-gray-500">Cargando programas...</p>
        </div>
      ) : programs.length === 0 ? (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-8 text-center">
          <GraduationCap className="h-12 w-12 text-blue-500 mx-auto mb-4" />
          <p className="text-blue-800 text-lg font-medium">No hay programas académicos registrados</p>
          <p className="text-blue-600 mt-2">Crea el primer programa para comenzar</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Código</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nombre</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nivel</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Duración</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Acciones</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {programs
                .filter((program) =>
                  program.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
                  program.name.toLowerCase().includes(searchTerm.toLowerCase())
                )
                .map((program) => (
                <tr key={program.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{program.code}</td>
                  <td className="px-6 py-4 text-sm text-gray-900">{program.name}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{LEVEL_LABELS[program.level]}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                    {program.duration ? `${program.duration} meses` : 'No definido'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        program.isActive
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {program.isActive ? 'Activo' : 'Inactivo'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={() => handleEdit(program)}
                      className="text-blue-600 hover:text-blue-900 mr-4"
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(program.id)}
                      className="text-red-600 hover:text-red-900"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingProgram ? 'Editar Programa' : 'Nuevo Programa'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4">
              <div>
                <Label htmlFor="code">Código *</Label>
                <Input
                  id="code"
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                  placeholder="Ej: LIC-COMP"
                  required
                />
              </div>

              <div>
                <Label htmlFor="name">Nombre *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Ej: Licenciatura en Computación"
                  required
                />
              </div>

              <div>
                <Label htmlFor="level">Nivel *</Label>
                <select
                  id="level"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  value={formData.level}
                  onChange={(e) => setFormData({ ...formData, level: e.target.value as any })}
                >
                  {Object.entries(LEVEL_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </div>

              <div>
                <Label htmlFor="duration">Duración (meses)</Label>
                <Input
                  id="duration"
                  type="number"
                  min="1"
                  value={formData.duration}
                  onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                  placeholder="Ej: 36"
                />
              </div>

              <div>
                <Label htmlFor="description">Descripción</Label>
                <textarea
                  id="description"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  rows={3}
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Descripción del programa académico"
                />
              </div>

              <div>
                <Label>Materias del Programa</Label>
                <div className="relative mt-2 mb-2">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    type="text"
                    placeholder="Buscar materias..."
                    value={courseSearchTerm}
                    onChange={(e) => setCourseSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
                <div className="border border-gray-300 rounded-md p-3 max-h-80 overflow-y-auto bg-gray-50">
                  {availableCourses.length === 0 ? (
                    <p className="text-sm text-gray-500">No hay materias disponibles</p>
                  ) : (
                    (() => {
                      const filteredCourses = availableCourses.filter(course =>
                        course.code.toLowerCase().includes(courseSearchTerm.toLowerCase()) ||
                        course.name.toLowerCase().includes(courseSearchTerm.toLowerCase())
                      );
                      
                      return filteredCourses.length === 0 ? (
                        <p className="text-sm text-gray-500">No se encontraron materias con ese criterio</p>
                      ) : (
                        <div className="space-y-3">
                          {filteredCourses.map((course) => {
                            const selected = isCourseSelected(course.id);
                            const sequence = getCourseSequence(course.id);
                            return (
                              <div key={course.id} className={`p-3 rounded border ${selected ? 'bg-blue-50 border-blue-200' : 'bg-white border-gray-200 hover:bg-gray-50'}`}>
                                <label className="flex items-center space-x-2 cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={selected}
                                    onChange={(e) => toggleCourse(course.id, e.target.checked)}
                                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                  />
                                  <span className="text-sm text-gray-700 flex-1">
                                    <span className="font-medium">{course.code}</span> - {course.name}
                                    {course.credits > 0 && <span className="text-gray-500 ml-1">({course.credits} créditos)</span>}
                                  </span>
                                </label>
                                {selected && (
                                  <div className="mt-2 ml-6 flex gap-3">
                                    <div className="flex-1">
                                      <label className="text-xs text-gray-500">Cadena/Bloque</label>
                                      <input
                                        type="text"
                                        placeholder="Ej: Cocina Caliente"
                                        value={sequence?.sequenceChainId || ''}
                                        onChange={(e) => updateCourseSequence(course.id, 'sequenceChainId', e.target.value || null)}
                                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                                      />
                                    </div>
                                    <div className="w-20">
                                      <label className="text-xs text-gray-500">Orden</label>
                                      <input
                                        type="number"
                                        min="1"
                                        placeholder="#"
                                        value={sequence?.sequenceOrder || ''}
                                        onChange={(e) => updateCourseSequence(course.id, 'sequenceOrder', e.target.value ? parseInt(e.target.value) : null)}
                                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                                      />
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      );
                    })()
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-1">Selecciona las materias y opcionalmente define su cadena de secuencia para planificación automática</p>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="isActive"
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                />
                <Label htmlFor="isActive" className="ml-2">
                  Programa activo
                </Label>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setIsDialogOpen(false);
                  resetForm();
                }}
              >
                Cancelar
              </Button>
              <Button type="submit">
                {editingProgram ? 'Actualizar' : 'Crear'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
